import { useState } from "react";
import { Type, Crown, Sun, Moon, Monitor, Lightbulb, ChefHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePreferences } from "@/hooks/usePreferences";
import { isWakeLockSupported } from "@/hooks/useWakeLock";
import { openUpgrade } from "@/lib/openUpgrade";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import type {
  Contrast,
  Preferences,
  RecipeFont,
  TextSize,
  UnitSystem,
} from "@/lib/preferences";

/**
 * Floating "Aa" button + Popover/Sheet of personalisation controls.
 * Only rendered when a user is signed in. Free-tier users see the button
 * with a crown badge and tapping it opens the upgrade dialog.
 */
export const PreferencesPanel = () => {
  const { signedInEmail, subscriptionActive, subscriptionChecked } = usePreferences();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Lift the Aa button above the recipe action bar (Share / Save) so it
  // doesn't sit on top of the buttons there.
  const onRecipePage =
    location.pathname.startsWith("/recipe") || location.pathname.startsWith("/r/");

  // Hide entirely until we know who the user is.
  if (!signedInEmail || !subscriptionChecked) return null;

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (!subscriptionActive) {
      e.preventDefault();
      openUpgrade(
        navigate,
        "default",
        () => {},
        window.location.pathname + window.location.search,
      );
    }
  };

  const triggerButton = (
    <button
      type="button"
      onClick={handleTriggerClick}
      aria-label="Display preferences"
      className={cn(
        "fixed right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full",
        "bg-primary text-primary-foreground shadow-lift transition-smooth",
        // Sit at reduced opacity so the button doesn't compete with content;
        // restore on hover/focus/active so it never feels broken.
        "opacity-70 hover:opacity-100 focus-visible:opacity-100 active:opacity-100",
        "hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        onRecipePage ? "bottom-24" : "bottom-6",
      )}
    >
      <span className="font-display text-xl font-semibold">Aa</span>
      {!subscriptionActive && (
        <span
          className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-background text-primary shadow-soft"
          aria-hidden
        >
          <Crown className="h-3.5 w-3.5" />
        </span>
      )}
    </button>
  );

  const panel = <PanelBody />;

  return (
    <>
      {subscriptionActive ? (
        isMobile ? (
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>{triggerButton}</SheetTrigger>
            <SheetContent
              side="bottom"
              className="flex max-h-[85vh] flex-col rounded-t-2xl p-0"
            >
              <SheetHeader className="shrink-0 border-b border-border px-5 py-4">
                <SheetTitle className="font-display text-left">Display</SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4">{panel}</div>
              <div
                className="shrink-0 border-t border-border bg-background px-5 py-3"
                style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
              >
                <Button className="w-full" onClick={() => setOpen(false)}>
                  Done
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            <PopoverContent
              align="end"
              side="top"
              sideOffset={12}
              className="w-80 p-0"
            >
              <div className="border-b border-border px-4 py-3">
                <h2 className="font-display text-base font-semibold">Display</h2>
              </div>
              <div className="max-h-[70vh] overflow-y-auto p-4">{panel}</div>
            </PopoverContent>
          </Popover>
        )
      ) : (
        triggerButton
      )}
    </>
  );
};

const PanelBody = () => {
  const { prefs, setPref, reset } = usePreferences();
  const wakeSupported = isWakeLockSupported();

  return (
    <div className="space-y-6">
      <Section title="Reading">
        <SegmentedControl<RecipeFont>
          label="Recipe font"
          value={prefs.font}
          onChange={(v) => setPref("font", v)}
          options={[
            { value: "serif", label: "Serif" },
            { value: "sans", label: "Sans" },
          ]}
        />
        <SegmentedControl<TextSize>
          label="Text size"
          value={prefs.textSize}
          onChange={(v) => setPref("textSize", v)}
          options={[
            { value: "sm", label: "Small" },
            { value: "md", label: "Medium" },
            { value: "lg", label: "Large" },
          ]}
        />
      </Section>

      <Section title="Appearance">
        <SegmentedControl<Contrast>
          label="Contrast"
          value={prefs.contrast}
          onChange={(v) => setPref("contrast", v)}
          options={[
            { value: "light", label: "Light", icon: <Sun className="h-3.5 w-3.5" /> },
            { value: "dark", label: "Dark", icon: <Moon className="h-3.5 w-3.5" /> },
            { value: "system", label: "Auto", icon: <Monitor className="h-3.5 w-3.5" /> },
          ]}
        />
      </Section>

      <Section title="Units">
        <SegmentedControl<UnitSystem>
          label="Measurement system"
          value={prefs.units}
          onChange={(v) => setPref("units", v)}
          options={[
            { value: "auto", label: "Auto" },
            { value: "metric", label: "Metric" },
            { value: "imperial", label: "Imperial" },
          ]}
        />
        <p className="text-xs text-muted-foreground">
          Recipes are converted on the fly. Original quantities aren't changed.
        </p>
      </Section>

      <Section title="Cooking">
        <ToggleRow
          icon={<ChefHat className="h-4 w-4" />}
          label="Keep screen awake"
          description={
            wakeSupported
              ? "Stops your screen sleeping while viewing a recipe."
              : "Not supported in this browser."
          }
          checked={prefs.cookingMode && wakeSupported}
          disabled={!wakeSupported}
          onChange={(v) => setPref("cookingMode", v)}
        />
        <ToggleRow
          icon={<Type className="h-4 w-4" />}
          label="Ingredient check-off"
          description="Tap an ingredient to tick it off."
          checked={prefs.ingredientCheckoff}
          onChange={(v) => setPref("ingredientCheckoff", v)}
        />
        <ToggleRow
          icon={<Lightbulb className="h-4 w-4" />}
          label="Step check-off"
          description="Tap a method step to mark it done."
          checked={prefs.stepHighlight}
          onChange={(v) => setPref("stepHighlight", v)}
        />
      </Section>

      <div className="flex justify-between border-t border-border pt-3">
        <Button variant="ghost" size="sm" onClick={reset} className="text-xs text-muted-foreground">
          Reset to defaults
        </Button>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h3 className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

type SegOption<T extends string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
};

function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div
        role="radiogroup"
        aria-label={label}
        className="inline-flex w-full overflow-hidden rounded-full border border-border bg-muted/40 p-0.5"
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-smooth",
                active
                  ? "bg-primary text-primary-foreground shadow-soft"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const ToggleRow = ({
  icon,
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) => {
  const inner = (
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-xl border border-border/60 bg-background px-3 py-2.5",
        disabled && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 text-primary">{icon}</span>
        <div>
          <div className="text-sm font-medium text-foreground">{label}</div>
          {description && (
            <div className="text-xs text-muted-foreground">{description}</div>
          )}
        </div>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{inner}</div>
        </TooltipTrigger>
        <TooltipContent>Not supported in this browser</TooltipContent>
      </Tooltip>
    );
  }
  return inner;
};
